import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { roleService } from "../../utils/services/role.service";
import { toast } from "react-toastify";

const RoleModal = ({ open, handleClose, onAddData, selectedRole }) => {
  const client_id = localStorage.getItem("client_id");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [],
    is_active: true,
  });

  const logged_in_user_roles = useMemo(
    () => JSON.parse(localStorage.getItem("roles") || "[]"),
    [],
  );

  const [formErrors, setFormErrors] = useState({});

  const [availablePermissions, setAvailablePermissions] = useState({
    "User Management": [
      { key: "user.create", label: "Create Users" },
      { key: "user.read", label: "View Users" },
      { key: "user.update", label: "Update Users" },
      { key: "user.delete", label: "Delete Users" },
    ],
    "Role Management": [
      { key: "role.create", label: "Create Roles" },
      { key: "role.read", label: "View Roles" },
      { key: "role.update", label: "Update Roles" },
      { key: "role.delete", label: "Delete Roles" },
    ],
    "Order Management": [
      { key: "order.create", label: "Create Orders" },
      { key: "order.read", label: "View Orders" },
      { key: "order.update", label: "Update Orders" },
      { key: "order.delete", label: "Delete Orders" },
      { key: "order.export", label: "Export Orders" },
    ],
    "Inventory Management": [
      { key: "inventory.create", label: "Create Inventory" },
      { key: "inventory.read", label: "View Inventory" },
      { key: "inventory.update", label: "Update Inventory" },
      { key: "inventory.delete", label: "Delete Inventory" },
    ],
    "Client Management": [
      { key: "client.create", label: "Create Clients" },
      { key: "client.read", label: "View Clients" },
      { key: "client.update", label: "Update Clients" },
      { key: "client.delete", label: "Delete Clients" },
    ],
    "Client Config": [
      { key: "client-config.create", label: "Create Client Config" },
      { key: "client-config.read", label: "View Client Config" },
      { key: "client-config.update", label: "Update Client Config" },
      { key: "client-config.delete", label: "Delete Client Config" },
    ],
    "Supplier Management": [
      { key: "supplier.create", label: "Create Suppliers" },
      { key: "supplier.read", label: "View Suppliers" },
      { key: "supplier.update", label: "Update Suppliers" },
      { key: "supplier.delete", label: "Delete Suppliers" },
    ],
    "Catalog Management": [
      { key: "catalog.create", label: "Create Catalog" },
      { key: "catalog.read", label: "View Catalog" },
      { key: "catalog.update", label: "Update Catalog" },
      { key: "catalog.delete", label: "Delete Catalog" },
    ],
    "Purchase Order Management": [
      { key: "purchase_order.create", label: "Create Purchase Orders" },
      { key: "purchase_order.read", label: "View Purchase Orders" },
      { key: "purchase_order.update", label: "Update Purchase Orders" },
      { key: "purchase_order.delete", label: "Delete Purchase Orders" },
    ],
    "ASN Management": [
      { key: "asn.create", label: "Create ASN" },
      { key: "asn.read", label: "View ASN" },
      { key: "asn.update", label: "Update ASN" },
      { key: "asn.delete", label: "Delete ASN" },
    ],
    "Billing Management": [
      { key: "billing.read", label: "View Billing" },
      { key: "billing.update", label: "Update Billing" },
    ],
    Reports: [
      { key: "reports.read", label: "View Reports" },
      { key: "reports.export", label: "Export Reports" },
      { key: "reports.analytics", label: "Analytics Access" },
    ],
    "System Settings": [
      { key: "setting.create", label: "Create Settings" },
      { key: "setting.read", label: "View Settings" },
      { key: "setting.update", label: "Update Settings" },
      { key: "setting.backup", label: "System Backup" },
      { key: "setting.delete", label: "Delete Settings" },
    ],
    "Package Management": [
      { key: "package-type.create", label: "Create Packages" },
      { key: "package-type.read", label: "View Packages" },
      { key: "package-type.update", label: "Update Packages" },
      { key: "package-type.delete", label: "Delete Packages" },
    ],
    "KAMAN Module": [
      { key: "email-generate", label: "Generate Email" },
      { key: "ask-dropskip", label: "Ask Dropskip" },
      { key: "inventory-forecast", label: "Inventory Forecast" },
      { key: "rate-chart-calculation", label: "Rate Chart Calculation" },
      { key: "package-selection", label: "Package Selection" },
    ],
    "Event Management": [{ key: "event.create", label: "Create Events" }],
  });

  useEffect(() => {
    let users_permissions = [];
    logged_in_user_roles.forEach((role) => {
      users_permissions = [
        ...new Set([...users_permissions, ...role.permissions]),
      ];
    });
    // Filter available permissions based on logged-in user's permissions
    const filteredPermissions = {};
    Object.entries(availablePermissions).forEach(([module, permissions]) => {
      const filteredModulePermissions = permissions.filter((permission) =>
        users_permissions.includes(permission.key),
      );
      if (filteredModulePermissions.length > 0) {
        filteredPermissions[module] = filteredModulePermissions;
      }
    });
    setAvailablePermissions(filteredPermissions);
  }, [logged_in_user_roles]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Role name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Role name must be at least 2 characters";
    }

    if (!formData.description.trim()) {
      errors.description = "Description is required";
    } else if (formData.description.trim().length < 5) {
      errors.description = "Description must be at least 5 characters";
    }

    if (formData.permissions.length === 0) {
      errors.permissions = "At least one permission must be selected";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (selectedRole) {
      setFormData({
        name: selectedRole?.name || "",
        description: selectedRole?.description || "",
        permissions: selectedRole?.permissions || [],
        is_active: selectedRole?.is_active ?? true,
      });
    } else {
      initialData();
    }
    return () => {
      initialData();
    };
  }, [selectedRole]);

  const initialData = () => {
    setFormData({
      name: "",
      description: "",
      permissions: [],
      is_active: true,
    });
    setFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePermissionChange = (permissionKey, checked) => {
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        permissions: [...prev.permissions, permissionKey],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => p !== permissionKey),
      }));
    }

    // Clear permissions error if user selects at least one
    if (formErrors.permissions && checked) {
      setFormErrors((prev) => ({
        ...prev,
        permissions: "",
      }));
    }
  };

  const handleSelectAllInModule = (modulePermissions, checked) => {
    const moduleKeys = modulePermissions.map((p) => p.key);

    if (checked) {
      // Add all module permissions
      setFormData((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...moduleKeys])],
      }));
    } else {
      // Remove all module permissions
      setFormData((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !moduleKeys.includes(p)),
      }));
    }
  };

  const isModuleFullySelected = (modulePermissions) => {
    const moduleKeys = modulePermissions.map((p) => p.key);
    return moduleKeys.every((key) => formData.permissions.includes(key));
  };

  const isModulePartiallySelected = (modulePermissions) => {
    const moduleKeys = modulePermissions.map((p) => p.key);
    return (
      moduleKeys.some((key) => formData.permissions.includes(key)) &&
      !isModuleFullySelected(modulePermissions)
    );
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await roleService.edit(selectedRole._id, {
        ...formData,
        client: client_id,
      });
      onAddData && onAddData();
      toast.success("Role updated successfully!");
      initialData();
      handleClose();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await roleService.create({ ...formData, client: client_id });
      onAddData && onAddData();
      toast.success("Role created successfully!");
      handleClose();
      initialData();
    } catch (e) {
      console.error(e);
    }
  };

  // Get permission labels for display
  const getPermissionLabel = (permissionKey) => {
    for (const module of Object.values(availablePermissions)) {
      const permission = module.find((p) => p.key === permissionKey);
      if (permission) return permission.label;
    }
    return permissionKey; // Fallback to key if label not found
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        initialData();
      }}
      maxWidth="lg"
      fullWidth
      aria-labelledby="form-dialog-title"
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          id="form-dialog-title"
          style={{
            fontFamily: "Poppins",
            fontSize: 24,
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          {selectedRole ? "Edit" : "Create"} Role
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />

      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
              }}
            >
              Basic Information
            </Typography>
          </Grid>

          {/* Role Name */}
          <Grid item xs={12} md={6}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Role Name <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
              placeholder="Enter role name"
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                  sx={{
                    color: "#0040FF",
                    "&.Mui-checked": {
                      color: "#0040FF",
                    },
                  }}
                />
              }
              label={
                <CustomTypography
                  style={{
                    fontSize: 14,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    color: "var(--text-heading)",
                  }}
                >
                  Active
                </CustomTypography>
              }
              sx={{ mt: 3 }}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "var(--text-heading)",
              }}
            >
              Description <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              style={{ backgroundColor: "var(--bg-paper)", marginTop: 8 }}
              size="small"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              fullWidth
              placeholder="Enter role description"
              error={!!formErrors.description}
              helperText={formErrors.description}
            />
          </Grid>

          {/* Permissions Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                color: "var(--text-tertiary)",
                mb: 2,
              }}
            >
              Permissions <span style={{ color: "red" }}>*</span>
            </Typography>

            {/* Selected Permissions Summary */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  color: "#667085",
                  mb: 1,
                }}
              >
                Selected Permissions: {formData.permissions.length}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {formData.permissions.slice(0, 10).map((permission) => (
                  <Chip
                    key={permission}
                    label={getPermissionLabel(permission)}
                    size="small"
                    sx={{
                      backgroundColor: "#E8F4FD",
                      color: "#0040FF",
                      fontSize: "10px",
                    }}
                  />
                ))}
                {formData.permissions.length > 10 && (
                  <Chip
                    label={`+${formData.permissions.length - 10} more`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--bg-input)",
                      color: "#667085",
                      fontSize: "10px",
                    }}
                  />
                )}
              </Box>
              {formErrors.permissions && (
                <Typography
                  sx={{
                    color: "#D32F2F",
                    fontSize: "0.75rem",
                    fontFamily: "Poppins",
                    mt: 1,
                  }}
                >
                  {formErrors.permissions}
                </Typography>
              )}
            </Box>

            {/* Permissions by Module */}
            {Object.entries(availablePermissions).map(
              ([module, permissions]) => (
                <Accordion key={module} sx={{ mb: 1 }}>
                  {console.log(module)}
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isModuleFullySelected(permissions)}
                            indeterminate={isModulePartiallySelected(
                              permissions,
                            )}
                            onChange={(e) =>
                              handleSelectAllInModule(
                                permissions,
                                e.target.checked,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: "#0040FF",
                              "&.Mui-checked": {
                                color: "#0040FF",
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              fontSize: 16,
                              color: "var(--text-tertiary)",
                            }}
                          >
                            {module}
                          </Typography>
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Box sx={{ ml: "auto", mr: 2 }}>
                        <Chip
                          label={`${
                            permissions.filter((p) =>
                              formData.permissions.includes(p.key),
                            ).length
                          }/${permissions.length}`}
                          size="small"
                          sx={{
                            backgroundColor: "var(--bg-input)",
                            color: "#667085",
                            fontSize: "10px",
                          }}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormGroup>
                      <Grid container spacing={1}>
                        {permissions.map((permission) => (
                          <Grid item xs={12} sm={6} md={3} key={permission.key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={formData.permissions.includes(
                                    permission.key,
                                  )}
                                  onChange={(e) =>
                                    handlePermissionChange(
                                      permission.key,
                                      e.target.checked,
                                    )
                                  }
                                  sx={{
                                    color: "#0040FF",
                                    "&.Mui-checked": {
                                      color: "#0040FF",
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontFamily: "Poppins",
                                    fontSize: 14,
                                    color: "var(--text-tertiary)",
                                  }}
                                >
                                  {permission.label}
                                </Typography>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              ),
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ paddingBottom: 15, paddingRight: 15 }}>
        <Button
          style={{
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            color: "#007DFF",
            textTransform: "none",
          }}
          variant="outlined"
          onClick={() => {
            handleClose();
            initialData();
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => (selectedRole ? handleEditData(e) : handleSave(e))}
          color="primary"
          variant="contained"
          style={{
            color: "#FFFFFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            width: 100,
            textTransform: "none",
          }}
        >
          {selectedRole ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleModal;
